import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import PolicyModal from "../components/PolicyModal";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import "./css/authstyles.css";
import "../assets/style.css";
import {
  fetchActivePolicies,
  findPolicyByTitle,
  PolicyItem,
} from "../utils/policyHelpers";

const SignIn = () => {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const navigate = useNavigate();
  const location = useLocation();
  const { showModal } = useMessageModal();

  const activationPending = location.state?.activationPending || false;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyItem | null>(null);

  useEffect(() => {
    const storedData =
      sessionStorage.getItem("userdata") ||
      localStorage.getItem("subscriber_user");

    if (storedData) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadPolicies = async () => {
      try {
        const data = await fetchActivePolicies(BASE);

        if (isMounted) {
          setPolicies(data);
        }
      } catch (error) {
        console.error("Failed to load policies:", error);
      }
    };

    loadPolicies();

    return () => {
      isMounted = false;
    };
  }, [BASE]);

  const openPrivacyPolicy = () => {
    const policy = findPolicyByTitle(policies, "Privacy Policy");

    if (policy) {
      setSelectedPolicy(policy);
    }
  };

  const openTermsPolicy = () => {
    const policy =
      findPolicyByTitle(policies, "Terms of Service") ||
      findPolicyByTitle(policies, "Terms of Services");

    if (policy) {
      setSelectedPolicy(policy);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    try {
      const response = await axios.post(
        `${BASE}/signin`,
        {
          email,
          password,
          rememberMe,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data?.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message: response.data?.message || "Unable to sign in.",
          confirmText: "OK",
        });
        return;
      }

      localStorage.setItem(
        "subscriber_user",
        JSON.stringify({
          ...response.data.user,
          tokenExpiry: response.data.tokenExpiry,
        }),
      );

      navigate("/");
    } catch (error: any) {
      console.error("Signin error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        confirmText: "OK",
      });
    }
  };

  return (
    <div className="auth-container">
      <img src="/images/Lyra-Zone-Logo-New-White.png" alt="LyraZone Logo" />

      <form
        onSubmit={handleSubmit}
        className="auth-bg-white p-6 rounded shadow-md w-96"
      >
        <h2 className="auth-text-2xl font-bold mb-4">
          {activationPending ? (
            <p
              style={{
                marginTop: "20px",
                color: "white",
                fontSize: "14px",
                animation: "none",
              }}
            >
              Please Check Your Email to Activate the Account!
            </p>
          ) : (
            <p className="alternate">Welcome Back !</p>
          )}

          <br />
          Sign In
        </h2>

        <input
          type="email"
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="password"
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
          required
          className="auth-border p-2 w-full mb-2"
        />

        {!activationPending ? (
          <button type="submit" className="sign-in-btn">
            Sign In
          </button>
        ) : (
          <button type="button" className="sign-in-btn disabled-btn" disabled>
            Sign In
          </button>
        )}
      </form>

      <div className="auth-general-text">
        <a href="/signup">Sign Up Now !</a>
        <a style={{ marginLeft: "50px" }} href="/forgot-password">
          Forgot Password?
        </a>
      </div>

      <div className="auth-remember-me">
        <input
          type="checkbox"
          id="rememberMe"
          checked={rememberMe}
          onChange={() => setRememberMe(!rememberMe)}
        />
        <label htmlFor="rememberMe" style={{ marginLeft: "5px" }}>
          Remember Me
        </label>
      </div>

      <div className="auth-general-terms">
        By signing in, you agree to our{" "}
        <span className="auth-policy-link" onClick={openPrivacyPolicy}>
          Privacy Policy
        </span>{" "}
        and{" "}
        <span className="auth-policy-link" onClick={openTermsPolicy}>
          Terms of Service
        </span>
        .
      </div>

      <PolicyModal
        isOpen={!!selectedPolicy}
        title={selectedPolicy?.title || ""}
        body={selectedPolicy?.body || ""}
        onClose={() => setSelectedPolicy(null)}
      />
    </div>
  );
};

export default SignIn;
